import { faChevronDown, faEdit, faGripLines, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, FloatingLabel, Form, Stack } from "react-bootstrap";
import {
  deleteLevelAssessmentType,
  deleteLevelGoalTemplate,
  deleteLevelNoteType,
  patchLevelAssessmentType,
  patchLevelGoalTemplate,
  patchLevelNoteType,
  patchProgramLevelAdvancement,
  postLevelAssessmentType,
  postLevelGoalTemplate,
  postLevelNoteType,
  reorderLevelAssessmentTypes,
  reorderLevelGoalTemplates,
  reorderLevelNoteTypes,
} from "../../../services/httpClient";
import Input from "../../ui/Input";
import IconButton from "../../ui/IconButton";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import SelectObjectTypeahead from "../../ui/SelectObjectTypeahead";

export interface CatalogOption {
  key: string | number;
  name: string;
  [key: string]: unknown;
}

export const ChevronButton = ({ open, onClick, tooltip }: { open: boolean; onClick: any; tooltip?: string }) => (
  <Button
    size="sm"
    variant="light"
    className="border p-0 flex-shrink-0"
    style={{ width: 28, height: 28 }}
    data-tooltip-id={tooltip ? "tooltip" : undefined}
    data-tooltip-content={tooltip}
    onClick={onClick}
  >
    <FontAwesomeIcon
      icon={faChevronDown}
      fontSize={12}
      className="app-primary-color-inverse"
      style={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.18s ease",
      }}
    />
  </Button>
);

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

interface SectionConfig {
  title: string;
  singular: string;
  itemsKey: string;
  catalogIdField: string;
  nameField: string;
  hasFrequency: boolean;
  groupBy?: "category" | "defaultCustom";
  post: any;
  patch: any;
  remove: any;
  reorder: any;
}

export const SECTIONS: SectionConfig[] = [
  {
    title: "Notes",
    singular: "Note Type",
    itemsKey: "noteTypes",
    catalogIdField: "notetype_id",
    nameField: "notetype_name",
    hasFrequency: false,
    groupBy: "defaultCustom",
    post: postLevelNoteType,
    patch: patchLevelNoteType,
    remove: deleteLevelNoteType,
    reorder: reorderLevelNoteTypes,
  },
  {
    title: "Goals",
    singular: "Goal Template",
    itemsKey: "goalTemplates",
    catalogIdField: "goal_template_id",
    nameField: "goal_template_name",
    hasFrequency: false,
    groupBy: "category",
    post: postLevelGoalTemplate,
    patch: patchLevelGoalTemplate,
    remove: deleteLevelGoalTemplate,
    reorder: reorderLevelGoalTemplates,
  },
  {
    title: "Assessments",
    singular: "Assessment Type",
    itemsKey: "assessmentTypes",
    catalogIdField: "assessmenttype_id",
    nameField: "assessmenttype_name",
    hasFrequency: true,
    post: postLevelAssessmentType,
    patch: patchLevelAssessmentType,
    remove: deleteLevelAssessmentType,
    reorder: reorderLevelAssessmentTypes,
  },
];

export interface LevelSummarySource {
  goalTemplates?: any[];
  assessmentTypes?: any[];
  noteTypes?: any[];
  min_days_before_advance?: number | null;
  advancement_notes?: string | null;
}

// "2 notes · 3 goals · 1 assessment · 14 days min", in the same order the sections appear
export const levelSummaryParts = (level: LevelSummarySource) => {
  const parts: string[] = [];
  if (level.noteTypes?.length) parts.push(plural(level.noteTypes.length, "note"));
  if (level.goalTemplates?.length) parts.push(plural(level.goalTemplates.length, "goal"));
  if (level.assessmentTypes?.length) parts.push(plural(level.assessmentTypes.length, "assessment"));
  if (level.min_days_before_advance !== null && level.min_days_before_advance !== undefined)
    parts.push(`${level.min_days_before_advance} days min`);
  return parts;
};

interface SectionState {
  config: SectionConfig;
  levelId: string;
  items: any[];
  catalog: CatalogOption[];
  getItems: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const LevelAttachSection = ({
  config,
  levelId,
  items,
  catalog,
  getItems,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: SectionState) => {
  const [list, setList] = useState<any[]>([]);
  const [deleteId, setDeleteId] = useState<string>();
  const [isAdding, setIsAdding] = useState(false);
  const [open, setOpen] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [freqDrafts, setFreqDrafts] = useState<{ [id: string]: string }>({});

  useEffect(() => {
    setList((items || []).map((i: any) => ({ ...i, name: i[config.nameField] })));
    setFreqDrafts({});
  }, [items]);

  const available = catalog.filter((c) => !list.some((i: any) => String(i[config.catalogIdField]) === String(c.key)));

  // goal templates group under their category (uncategorized last); note types group as Default / Custom
  const groupedOptions = () => {
    const byGroup: Record<string, any[]> = {};
    available.forEach((o: any) => {
      const groupName =
        config.groupBy === "defaultCustom" ? (o.organization_id === null ? "Default" : "Custom") : o.category_name || "Uncategorized";
      (byGroup[groupName] ||= []).push({
        value: o.key,
        label: o.name,
        original: o,
      });
    });
    const byLabel = (a: any, b: any) => a.label?.localeCompare(b.label);
    const rank = (name: string) => {
      if (config.groupBy === "defaultCustom") return name === "Default" ? 0 : 1;
      return name === "Uncategorized" ? 1 : 0;
    };
    return Object.keys(byGroup)
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
      .map((name) => ({
        key: name,
        name,
        label: name,
        options: byGroup[name].sort(byLabel),
      }));
  };

  const isRequired = (i: any) => i.is_required === 1 || i.is_required === true;
  const onError = (err: any) => setErrMessage(err?.response?.data?.msg);

  const defaultFrequencyText = (i: any) => {
    // a level-specific frequency overrides the assessment type's default
    const override = freqDrafts[i.id] ?? i.frequency_override;
    if (override !== null && override !== undefined && override !== "") {
      return `Every ${override} days`;
    }
    const freq = catalog.find((c) => String(c.key) === String(i[config.catalogIdField]))?.frequency;
    return freq ? `Default: every ${freq} days` : "Default: As Needed";
  };

  const onAdd = (selected: CatalogOption | null) => {
    if (!selected) return;
    setIsAdding(false);
    setErrMessage("");
    const payload: any = { [config.catalogIdField]: selected.key };
    // prefill the level's frequency with the assessment type's default, when it has one
    if (config.hasFrequency && Number(selected.frequency) > 0) {
      payload.frequency_override = Number(selected.frequency);
    }
    config
      .post(levelId, payload)
      .then(() => {
        setShowToast(true);
        setToastText(config.singular + " Added");
        setToastBody(selected.name);
        getItems(true);
      })
      .catch(onError);
  };

  const onConfirmRemove = (id: string) => {
    const name = list.find((i: any) => i.id === id)?.name;
    setDeleteId(undefined);
    config
      .remove(levelId, id)
      .then(() => {
        setShowToast(true);
        setToastText(config.singular + " Removed");
        setToastBody(name);
        getItems(true);
      })
      .catch(onError);
  };

  const onToggleRequired = (id: string, checked: boolean) => {
    setList(list.map((i: any) => (i.id === id ? { ...i, is_required: checked ? 1 : 0 } : i)));
    const name = list.find((i: any) => i.id === id)?.name;
    config
      .patch(levelId, id, { is_required: checked })
      .then(() => {
        setShowToast(true);
        setToastText(checked ? "Marked Required" : "Marked Optional");
        setToastBody(name);
        getItems(true);
      })
      .catch((err: any) => {
        onError(err);
        getItems(true); // revert the optimistic checkbox
      });
  };

  const onSaveFrequency = (item: any) => {
    const draft = freqDrafts[item.id];
    if (draft === undefined) return;
    const next = draft === "" ? null : Number(draft);
    if (next === (item.frequency_override ?? null)) return;
    config
      .patch(levelId, item.id, { frequency_override: next })
      .then(() => {
        setShowToast(true);
        setToastText("Frequency Updated");
        setToastBody(item.name);
        getItems(true);
      })
      .catch((err: any) => {
        onError(err);
        getItems(true); // discard the unsaved draft
      });
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    const reordered = [...list];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setList(reordered);
    config
      .reorder(
        levelId,
        reordered.map((i: any) => i.id),
      )
      .then(() => {
        setShowToast(true);
        setToastText(config.title + " Order");
        setToastBody("The order has been updated.");
        getItems(true);
      })
      .catch(onError);
  };

  const renderRemove = (item: any) => (
    <div key={"remove-" + item.id} className="bg-light border rounded p-2 mb-2">
      <Stack direction="horizontal" gap={3}>
        <small className="me-auto">Remove {item.name} from this level?</small>
        <Button className="app-danger-color button" onClick={() => onConfirmRemove(item.id)}>
          Confirm Remove
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={() => setDeleteId(undefined)}>
          Cancel
        </Button>
      </Stack>
    </div>
  );

  return (
    <div className="mt-4">
      <Stack direction="horizontal" gap={3} className="mb-2">
        <ChevronButton open={open} onClick={() => setOpen(!open)} tooltip={open ? "Collapse" : "Expand"} />
        <h5 className="mb-0 me-auto app-header-text">
          {config.title}{" "}
          <Badge bg="none" className="app-secondary-color align-middle ms-1" style={{ fontSize: "0.6em" }}>
            {list.length}
          </Badge>
        </h5>
        <IconEmbeddedButton
          onClickButton={() => {
            setOpen(true);
            setIsAdding(!isAdding);
          }}
          tooltipText={"Add " + config.singular}
          icon={faPlus}
        />
      </Stack>
      {open &&
        list.map((item: any, index: number) =>
          deleteId === item.id ? (
            renderRemove(item)
          ) : (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                setDraggedIndex(index);
                e.currentTarget.classList.add("dragging");
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (draggedIndex !== null && draggedIndex !== index) setDropTargetIndex(index);
              }}
              onDragLeave={() => setDropTargetIndex(null)}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDrop(index);
                setDropTargetIndex(null);
                setDraggedIndex(null);
              }}
              onDragEnd={(e) => {
                e.currentTarget.classList.remove("dragging");
                setDraggedIndex(null);
                setDropTargetIndex(null);
              }}
              className={"bg-light border rounded p-2 mb-2" + (dropTargetIndex === index ? " drop-target" : "")}
            >
              <Stack direction="horizontal" gap={3}>
                {list.length > 1 && (
                  <span
                    className="ms-2"
                    style={{ fontSize: "1rem", cursor: "grab" }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={"Drag to Reorder"}
                  >
                    <FontAwesomeIcon icon={faGripLines} className="text-muted" />
                  </span>
                )}
                <div className={"me-auto" + (list.length > 1 ? "" : " ms-2")} style={{ minWidth: 0 }}>
                  <div className="small text-truncate">{item.name}</div>
                  {config.hasFrequency && <div className="text-muted small">{defaultFrequencyText(item)}</div>}
                </div>
                <Form.Check
                  type="checkbox"
                  id={`required-${config.itemsKey}-${item.id}`}
                  label="Required"
                  checked={isRequired(item)}
                  style={{ accentColor: "#da7e39" }}
                  onChange={(e: any) => onToggleRequired(item.id, e.target.checked)}
                />
                {config.hasFrequency && (
                  <FloatingLabel label="Frequency (days)" style={{ width: "11rem" }}>
                    <Form.Control
                      type="number"
                      min={0}
                      placeholder="Use default"
                      value={
                        freqDrafts[item.id] ??
                        (item.frequency_override === null || item.frequency_override === undefined ? "" : String(item.frequency_override))
                      }
                      onChange={(e: any) =>
                        setFreqDrafts({
                          ...freqDrafts,
                          [item.id]: e.target.value,
                        })
                      }
                      onBlur={() => onSaveFrequency(item)}
                    />
                  </FloatingLabel>
                )}
                <IconButton className="me-2" onClickButton={() => setDeleteId(item.id)} tooltipText={"Remove"} icon={faTrash} />
              </Stack>
            </div>
          ),
        )}
      {open && isAdding && (
        <SelectObjectTypeahead
          label={"Add " + config.singular}
          value={null}
          options={config.groupBy ? groupedOptions() : available}
          onUpdate={onAdd}
          groupHeading={!!config.groupBy}
          focused={true}
        />
      )}
    </div>
  );
};

interface LevelDetailState {
  level: any;
  goalTemplateCatalog: CatalogOption[];
  assessmentTypeCatalog: CatalogOption[];
  noteTypeCatalog: CatalogOption[];
  getItems: any;
  onEditFinal: (levelId: any, name: string) => void;
  onDeleteFinal: (levelId: any, name: string) => void;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

// the right-hand panel of the program editor: one level's name, attached items and advancement criteria
const ProgramLevelCard = ({
  level,
  goalTemplateCatalog,
  assessmentTypeCatalog,
  noteTypeCatalog,
  getItems,
  onEditFinal,
  onDeleteFinal,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: LevelDetailState) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [levelName, setLevelName] = useState<string>(level.name);
  const [advOpen, setAdvOpen] = useState(false);
  const [minDays, setMinDays] = useState<string>("");
  const [advNotes, setAdvNotes] = useState<string>("");

  useEffect(() => {
    setLevelName(level.name);
    setMinDays(
      level.min_days_before_advance === null || level.min_days_before_advance === undefined ? "" : String(level.min_days_before_advance),
    );
    setAdvNotes(level.advancement_notes || "");
  }, [level]);

  const hasMinDays = level.min_days_before_advance !== null && level.min_days_before_advance !== undefined;
  const hasNotes = !!level.advancement_notes;

  const advParts: string[] = [];
  if (hasMinDays) advParts.push(`${level.min_days_before_advance} days minimum`);
  if (hasNotes) advParts.push("notes added");

  const onSaveAdvancement = () => {
    setErrMessage("");
    patchProgramLevelAdvancement(level.id, level.name, {
      min_days_before_advance: minDays === "" ? null : Number(minDays),
      advancement_notes: advNotes.trim() === "" ? null : advNotes,
    })
      .then(() => {
        setShowToast(true);
        setToastText("Advancement Criteria Updated");
        setToastBody(level.name);
        getItems(true);
      })
      .catch((err: any) => setErrMessage(err?.response?.data?.msg));
  };

  const catalogs: CatalogOption[][] = [noteTypeCatalog, goalTemplateCatalog, assessmentTypeCatalog];

  const renderHeader = () => {
    if (isDeleting) {
      return (
        <Stack direction="horizontal" gap={3}>
          <Stack style={{ justifyContent: "center" }} gap={2}>
            <Card.Subtitle>Delete {level.name}?</Card.Subtitle>
          </Stack>
          <Button
            className="app-danger-color button"
            onClick={() => {
              setIsDeleting(false);
              onDeleteFinal(level.id, level.name);
            }}
          >
            Confirm Delete
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={() => setIsDeleting(false)}>
            Cancel
          </Button>
        </Stack>
      );
    }
    if (isEditing) {
      return (
        <Stack direction="horizontal" gap={3}>
          <Col>
            <Input label="Level" type="text" value={levelName} hasFocus={true} onUpdate={(e: any) => setLevelName(e.target.value)} />
          </Col>
          <Button
            className="app-secondary-color button"
            disabled={!levelName?.trim()}
            onClick={() => {
              if (!levelName || levelName.trim() === "") {
                setErrMessage("Please enter a value.");
                return;
              }
              setIsEditing(false);
              onEditFinal(level.id, levelName);
            }}
          >
            Save
          </Button>
          <div className="vr" />
          <Button
            className="app-primary-color button"
            onClick={() => {
              setIsEditing(false);
              setLevelName(level.name);
            }}
          >
            Cancel
          </Button>
        </Stack>
      );
    }
    return (
      <Stack direction="horizontal" gap={3}>
        <div className="me-auto fs-5 text-truncate">{level.name}</div>
        <IconButton onClickButton={() => setIsEditing(true)} tooltipText={"Rename Level"} icon={faEdit} />
        <div className="vr" />
        <IconButton onClickButton={() => setIsDeleting(true)} tooltipText={"Delete Level"} icon={faTrash} />
      </Stack>
    );
  };

  return (
    <Card>
      <Card.Body>
        {renderHeader()}
        {SECTIONS.map((config, i) => (
          <LevelAttachSection
            key={config.itemsKey}
            config={config}
            levelId={level.id}
            items={level[config.itemsKey]}
            catalog={catalogs[i] || []}
            getItems={getItems}
            setErrMessage={setErrMessage}
            setShowToast={setShowToast}
            setToastText={setToastText}
            setToastBody={setToastBody}
          />
        ))}
        <Card className="bg-light mt-4">
          <Card.Body>
            <Stack direction="horizontal" gap={3} className="align-items-start">
              <ChevronButton open={advOpen} onClick={() => setAdvOpen(!advOpen)} />
              <div className="cursor-pointer" onClick={() => setAdvOpen(!advOpen)}>
                <h5 className="mb-0 app-header-text">Advancement Criteria</h5>
                <div className="text-muted small">{advParts.length > 0 ? advParts.join(" · ") : "None set"}</div>
              </div>
            </Stack>
            {advOpen && (
              <Stack gap={3} className="mt-3">
                <Col xs={12} md={5}>
                  <Input
                    label="Minimum days before advancing"
                    type="number"
                    value={minDays}
                    onUpdate={(e: any) => setMinDays(e.target.value)}
                  />
                </Col>
                <FloatingLabel label="Advancement notes">
                  <Form.Control
                    as="textarea"
                    placeholder="Advancement notes"
                    value={advNotes}
                    onChange={(e: any) => setAdvNotes(e.target.value)}
                    style={{ height: "9rem" }}
                  />
                </FloatingLabel>
                <Stack direction="horizontal" gap={3}>
                  <Button className="app-secondary-color button" onClick={onSaveAdvancement}>
                    Save
                  </Button>
                  <small className="text-muted">Informational only for now: nothing evaluates these yet.</small>
                </Stack>
              </Stack>
            )}
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
};

export default ProgramLevelCard;
